import {
  Stack,
  router,
  useGlobalSearchParams,
  useLocalSearchParams,
  useNavigation,
} from 'expo-router';
import { useEffect, useMemo } from 'react';
import { StyleSheet } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import { useRemoveOffer } from '@fhs/loyalty/src/hooks/use-remove-offer';
import { useRewardTierApplied } from '@fhs/loyalty/src/hooks/use-reward-tier-applied';
import { useRemoveIncentiveStore } from '@fhs/loyalty/src/modals';
import { getScreenTitleHeader } from '@fhs/routing/src/components/get-screen-title-header';
import { IconArrowBack, MqSwitch, Pressable, tokens } from '@fhs/ui';
import { selectors, useAppSelector } from '@fhs-legacy/frontend/src/state/global-state';
import { useOrderContext } from '@fhs-legacy/frontend/src/state/order';
import { useStoreContext } from '@fhs-legacy/frontend/src/state/store';
import LocalStorage, { StorageKeys } from '@fhs-legacy/frontend/src/utils/local-storage';
import { parseStringifiedJSON } from '@fhs-legacy/frontend/src/utils/parse-string';

import { ComboSelectionProvider, type SelectedPicker } from '../state/combo-selection';
import { MenuLayoutProvider } from '../state/menu-layout';
import { MenuPickerProvider } from '../state/menu-picker';
import { useEditFromCartState } from '../utils/use-edit-from-cart-state';
export { ErrorBoundary } from '@fhs/routing/src/components/page-dev-error-boundary';

export default function MenuItemLayout() {
  const { slug } = useGlobalSearchParams<{ slug: string }>();

  const { updateCartEntry, setCartIdEditing } = useOrderContext();
  const {
    query: editFromCartQuery,
    cartEntry: editFromCartCartEntry,
    isEditingThisItem,
  } = useEditFromCartState();

  const { show: showRemoveIncentiveModal } = useRemoveIncentiveStore();
  const { removeOffer } = useRemoveOffer();
  const incentiveSelections = useAppSelector(selectors.loyalty.selectSelectedOfferSelections);

  const incentiveSelection = useMemo(() => {
    return incentiveSelections?.find(
      selection =>
        editFromCartCartEntry?.lineId && selection.lineId === editFromCartCartEntry.lineId
    );
  }, [incentiveSelections, editFromCartCartEntry?.lineId]);

  const { rewardTierApplied } = useRewardTierApplied();

  const navigation = useNavigation();

  const { noStoreSelected } = useStoreContext();

  useEffect(() => {
    // Clear editing cart id on unmount
    const unsubscribe = navigation.addListener('beforeRemove', () => {
      setCartIdEditing('');
    });

    return unsubscribe;
  }, [navigation, setCartIdEditing]);

  const editFromCartComboSelectedPickers = useMemo(() => {
    if (!isEditingThisItem || !editFromCartCartEntry) {
      return undefined;
    }

    const existingSelectedPickers: Record<
      string,
      Record<string, SelectedPicker>
    > = parseStringifiedJSON({
      value: LocalStorage.getItem(StorageKeys.ORDER_COMBO_SELECTED_PICKERS),
      defaultValue: {},
    });

    return existingSelectedPickers?.[editFromCartCartEntry.cartId] ?? undefined;
  }, [editFromCartCartEntry, isEditingThisItem]);

  return (
    <MenuLayoutProvider
      value={{
        addText: noStoreSelected ? 'Order Now' : isEditingThisItem ? 'Update' : 'Add to Order',
        isEditingThisItem: isEditingThisItem,
        onUpdate: cartEntry => {
          const update = () => {
            updateCartEntry(
              {
                ...cartEntry,
                // override generated cartId with the cart id of the item we're editing so it
                //  replaces it in the cart instead of adding another entry.
                cartId: editFromCartCartEntry.cartId,
                lineId: editFromCartCartEntry.lineId,
              },
              editFromCartCartEntry
            );

            router.replace('/cart');
            return;
          };

          const shouldRemoveRewardApplied =
            rewardTierApplied &&
            incentiveSelection?.incentiveId === rewardTierApplied.id &&
            !rewardTierApplied.eligibleItems?.some(eligibleItem => eligibleItem === cartEntry._id);

          if (shouldRemoveRewardApplied) {
            showRemoveIncentiveModal({
              offerName: rewardTierApplied.name,
              description:
                'Your reward will be removed from your cart, are you sure you want to continue?',
              heading: 'Remove Reward',
              callbacks: {
                onConfirm: () => {
                  removeOffer({ showDialog: false });
                  update();
                },
              },
            });
            return;
          }

          update();
        },
      }}
    >
      <MenuPickerProvider
        slug={slug}
        selectedOfferId={incentiveSelection?.incentiveId}
        isInitialStateLoading={editFromCartQuery.isLoading}
        initialState={
          isEditingThisItem
            ? {
                selectedPickerAspectOptionId:
                  editFromCartQuery.data?.selectedPickerAspectOptionId ?? undefined,
                userCustomizationSelections: editFromCartQuery.data?.userCustomizationSelections,
                instructions: editFromCartQuery.data?.instructions,
              }
            : undefined
        }
      >
        <ComboSelectionProvider
          initialState={
            isEditingThisItem
              ? {
                  isComboEnabled: editFromCartQuery.data?.isComboEnabled,
                  comboSelectedPickers: editFromCartComboSelectedPickers,
                }
              : undefined
          }
        >
          <SafeAreaView edges={['bottom', 'left', 'right']} style={styles.safeArea}>
            <Stack>
              <Stack.Screen
                name="index"
                options={{
                  header: props => (
                    <MqSwitch
                      $ltDesktop={() => getScreenTitleHeader()(props)}
                      $gteDesktop={() => <MenuItemDesktopHeader />}
                    />
                  ),
                  title: '',
                }}
              />
              <Stack.Screen
                name="customize"
                options={{
                  header: props => getScreenTitleHeader()(props),
                  title: 'Customize Item',
                  headerTitleAlign: 'center',
                  headerShadowVisible: false,
                }}
              />
            </Stack>
          </SafeAreaView>
        </ComboSelectionProvider>
      </MenuPickerProvider>
    </MenuLayoutProvider>
  );
}

const MenuItemDesktopHeader = () => {
  const { isFromCart } = useLocalSearchParams<{ isFromCart: string }>();

  return (
    <Pressable
      borderRadius={100}
      borderWidth={1}
      style={styles.iconContainer}
      accessibilityRole="button"
      accessibilityLabel="Go back"
      accessibilityHint={
        isFromCart === 'true' ? 'Returns to your cart' : 'Returns to the previous screen'
      }
      onPress={() => {
        if (isFromCart === 'true') {
          router.dismissTo('/cart');
        } else {
          router.back();
        }
      }}
    >
      <IconArrowBack
        accessible={false}
        accessibilityElementsHidden
        importantForAccessibility="no"
        size={24}
      />
    </Pressable>
  );
};

const styles = StyleSheet.create({
  safeArea: {
    backgroundColor: tokens.colors.$white,
    flex: 1,
  },
  iconContainer: {
    backgroundColor: tokens.colors.$white,
    height: 48,
    width: 48,
    justifyContent: 'center',
    alignItems: 'center',
    position: 'absolute',
    borderColor: tokens.colors.$black10,
    shadowColor: tokens.colors.$black,
    shadowOffset: { width: 0, height: 20 },
    shadowOpacity: 0.1,
    shadowRadius: 30,
    top: 19,
    left: 20,
  },
});
